<template>
  <div
    class="circle-content circle-content--default circle-base__content--shadow"
  >
    <lazy-img :src="step.image" class="circle-content__img" />
    <div class="portion__wrap">
      <div class="portion__content" ref="controlsContainer">
        <LineChart
          v-if="showChart"
          class="portion-chart"
          :style="[
            { '--t': (circleSize * 0.75) / ((chartLabels.length - 1) * 60) },
          ]"
          :change="change"
          :chart-data="chartList"
          :width="(chartLabels.length - 1) * 60"
          :height="(circleSize / 2) * 0.75"
        />
        <template v-for="(item, index) of list">
          <template v-if="item.roastlevel[getFormData.roastin]">
            <div class="portion__item" :key="index">
              <div class="portion__label">
                <span>{{ item.name }}</span>
              </div>
              <div class="portion__control control">
                <div
                  class="control__line"
                  :class="[{ 'control__line--arrow': +item.value > 0 }]"
                ></div>
                <input
                  class="control__slider"
                  type="range"
                  min="0"
                  max="100"
                  :value="item.value"
                  @input="setPortionValue(index, parseInt($event.target.value))"
                />
                <div
                  class="control__point"
                  :class="[{ 'control__point--active': +item.value > 0 }]"
                  :style="{
                    left: `${+item.value}%`,
                    fontSize: `${item.value / 7.5 + pointSize}px`,
                  }"
                ></div>
              </div>
            </div>
          </template>
        </template>
      </div>
    </div>
    <div class="circle-content__title fade-in" v-html="$t(step.title)"></div>
  </div>
</template>

<script>
import Circle from '@/class/Circle';
import { getFromStore } from '@/data/local-store';
import { localStoreSelectedCoffeeName } from '@/models/local-store';
import { Steps } from '@/models/steps';

let interval;

export default new Circle(Steps.PORTION, {
  data() {
    return {
      pointSize: 14,
      values: [],
      list: [],
      percent: 0,
      fully: 0,
      change: 0,
      chartLabels: [],
      chartValues: [],
      showChart: false,
    };
  },
  mounted() {
    this.list =
      (getFromStore(localStoreSelectedCoffeeName) ||
      this.$store.getters.getCoffeeList)
        .map((item, index) => {
          item.arrIndex = index;
          return item;
        })
        .filter((item, index) => !item.only_debutant);
    this.list.forEach((item, index) => {
      this.fully += item.value;
      this.values[index] = item.value;
      this.chartLabels[index] = index;
    });
    this.chartValues = this.values;
    this.showChart = this.list.length > 0;
  },
  watch: {
    circleSize(value) {
      this.showChart = false;
      setTimeout(() => {
        this.showChart = true;
      }, 300);
    },
  },
  computed: {
    chartList() {
      return {
        labels: this.chartLabels,
        datasets: [
          {
            borderWidth: 0,
            backgroundColor: 'rgba(121, 172, 43, 80%)',
            pointRadius: 0,
            data: this.chartValues,
            capBezierPoints: false,
            stepped: false,
            spanGaps: false,
            lineTension: false,
            cubicInterpolationMode: 'monotone',
          },
        ],
      };
    },
  },
  methods: {
    setPortionValue(index, value) {
      const increment = this.increment(index, value);
      const afterFully = this.fully + increment;

      if (afterFully <= 100 || increment < 0) {
        this.fully = afterFully;
      } else if (afterFully > 100) {
        value = value - (afterFully - 100);
        this.fully = 100;
      }

      this.setValues(value, index);
    },

    setValues(value, index) {
      this.values[index] = value;
      this.list[index].value = this.values[index];
      this.$store.commit('setListValue', { index: this.list[index].arrIndex, value: this.values[index] });

      this.setValue(this.fully === 100 ? 'true' : null);

      this.chartValues = this.values;
      clearTimeout(interval);
      interval = setTimeout(() => {
        this.change = (value + this.fully) * (index + 1);
      }, 100);
    },

    increment(index, value) {
      return value - this.values[index];
    },
  },
}).comp;
</script>

<style lang="scss">
.portion__wrap {
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;

  border-radius: 50%;
  box-shadow: inset 0 0 100px $brownMedium, inset 0 0 80px $brownMedium,
    inset 0 0 60px $brownMedium;

  &::before {
    content: '';
    display: block;
    width: 2px;
    height: 100%;
    position: absolute;
    top: 0;
    bottom: 0;
    background-color: white;
    left: 50%;
    transform: translateX(-1px);
    z-index: -1;
  }
}

.portion__content {
  box-sizing: border-box;
  padding: 100px 50px 40%;
  margin: -20px;
  height: 100%;
  overflow: auto;
  position: relative;
}

.portion__item {
  width: 86%;
  margin: 0 7%;
  display: flex;
  box-sizing: border-box;
}

.portion__label,
.portion__control {
  flex-basis: 50%;
}

.portion__label {
  font-size: 12px;
  line-height: 16px;
  text-align: right;
  height: 60px;
  box-sizing: border-box;
  position: relative;

  span {
    position: absolute;
    padding-right: 15px;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
  }
}

.control {
  position: relative;
}

.control__line {
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 1px;
  background-color: white;

  &::before,
  &::after {
    content: '';

    width: 10px;
    height: 10px;

    display: block;
    position: absolute;
    top: 0;
    transform: translateY(-50%);

    transition: all 200ms ease-out;
  }
}

.control__line:not(.control__line--arrow) {
  &::before,
  &::after {
    background-color: white;
    border-radius: 50%;
  }

  &::before {
    left: -5px;
  }

  &::after {
    right: 0;
  }
}

.control__line--arrow {
  &::before,
  &::after {
    width: 0;
    height: 0;
  }

  &::before {
    left: 0;
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;

    border-right: 10px solid white;
  }

  &::after {
    right: -2px;
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;

    border-left: 10px solid white;
  }
}

$pointSize: 12px;
.control__slider {
  appearance: none;
  position: absolute;
  left: -($pointSize / 2);
  right: -$pointSize;
  width: calc(100% + #{$pointSize});
  top: 50%;
  height: 1px;
  background-color: transparent;
  outline: none;
  margin: 0;
  transition: opacity 200ms;

  &:hover {
    opacity: 1;
  }
}

@mixin range-thumb {
  appearance: none;
  width: $pointSize * 2;
  height: $pointSize * 2;
  opacity: 0;
  cursor: pointer;
}

.control__slider::-webkit-slider-thumb {
  @include range-thumb;
}
.control__slider::-moz-range-thumb {
  @include range-thumb;
}

.control__point {
  width: 1em;
  height: 1em;
  background: $brownMedium;
  border: 2px solid white;
  border-radius: 50%;
  opacity: 1;
  pointer-events: none;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
}

.control__point--active {
  background-color: $green;

  transition: color 200ms ease-in-out, left 100ms ease-out;
}

.portion-chart {
  position: absolute;
  transform: rotate(90deg) translate(0, -50%);
  transform-origin: left center;
  top: 55px;
  // display: none;
  left: calc(50% + 1px);
}


@media (max-width: 460px) {
  .portion__content {
    padding: 70px 40px 140px;
  }

  .portion-chart {
    top: calc(58px - var(--t) * 25vw);
  }
}

@media (max-width: 430px) {
  .portion-chart {
    top: calc(60px - var(--t) * 25vw);
  }
}

@media (max-width: 415px) {
  .portion-chart {
    top: calc(62px - var(--t) * 25vw);
  }
}

@media (max-width: 380px) {
  .portion-chart {
    top: calc(64px - var(--t) * 25vw);
  }
}

@media (max-width: 340px) {
  .portion-chart {
    top: calc(66px - var(--t) * 25vw);
  }
}

@media (min-width: 460.89px) {
  .portion__label {
    font-size: 14px;
    line-height: 18px;
  }
}

@media (min-width: 640.89px) {
  .portion__label {
    font-size: 16px;
    line-height: 20px;

    span {
      padding-right: 20px;
    }
  }
}

@media (min-width: 992.89px) {
  .portion-chart {
    top: 35px;
  }
}
</style>
